// Dyrejournal MCP proposals (Dyrejournal-koden, A; plan docs/superpowers/plans/2026-10-21-partner-mcp-dj-proposals.md).

import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';
import util from 'preact-util';

import { routeForCreated } from '../../lib/mcpProposalNavigate';

const OPEN_STATUSES = ['pending', 'partial'];

// Pending and partial first, newest first. Sorts a copy: the store array is never touched.
function sortProposals(list) {
    const rank = p => (OPEN_STATUSES.includes(p.status) ? 0 : 1);
    return (list || []).slice().sort((a, b) => {
        if (rank(a) !== rank(b)) {
            return rank(a) - rank(b);
        }
        return new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime();
    });
}

// The clinic user who connected the AI client, from customer.members ([{ id, firstname, lastname }]).
function connectedUserName(members, userId) {
    const member = (members || []).find(m => m.id === userId);
    const name = member ? `${member.firstname || ''} ${member.lastname || ''}`.trim() : '';
    if (name) {
        return name;
    }
    return userId ? `#${userId}` : '';
}

// The last step that failed when the proposal was applied ({ step, id, ok, message }).
function lastFailure(results) {
    const failed = (results || []).filter(r => r && r.ok === false);
    return failed.length > 0 ? failed[failed.length - 1] : null;
}

// Bottom drawer behind the «Forslag fra AI» dashboard card (proposalsCard.js).
// «Opprett» / «Fortsett» applies the proposal with the user's own session and routes to
// what was created so the vet can check it; «Avvis» dismisses it.
@observer
class ProposalsDrawer extends Component {
    componentDidMount() {
        const { mcpProposalStore } = this.props.stores;
        mcpProposalStore.load();
    }

    onApply = async (proposal) => {
        const { mcpProposalStore, appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const response = await mcpProposalStore.apply(proposal.id);
        if (response && response.status === 200 && response.data && !response.data.failedStep) {
            route(routeForCreated(proposal.type, response.data.created));
            appState.toggleDrawer(false, drawerLevel);
        }
    }

    onDismiss = (proposal) => {
        const { mcpProposalStore } = this.props.stores;
        mcpProposalStore.dismiss(proposal.id);
    }

    render() {
        const { mcpProposalStore, userStore } = this.props.stores;
        const { mcpProposals = [], applying = {}, error = '' } = mcpProposalStore;
        const members = userStore.customer?.members || [];
        const proposals = sortProposals(mcpProposals);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <div class='font-weight-lighter px-3 box-header d-flex flex-row align-items-center'>
                        <i class='fa-duotone fa-wand-magic-sparkles me-2' />
                        <Text id='mcpProposals.title'>Proposals from AI</Text>
                    </div>

                    {!!error && (
                        <div class='px-3 mt-3'>
                            <div class='alert alert-danger mb-0'>
                                <i class='fa-solid fa-triangle-exclamation me-2' />
                                {error}
                            </div>
                        </div>
                    )}

                    <div class='px-3 py-3'>
                        {proposals.length === 0 ? (
                            <p class='text-body-secondary mb-0'>
                                <Text id='mcpProposals.empty'>No proposals</Text>
                            </p>
                        ) : (
                            <div class='list-group'>
                                {proposals.map((proposal) => {
                                    const isPartial = proposal.status === 'partial';
                                    const busy = !!applying[proposal.id];
                                    const failure = lastFailure(proposal.results);
                                    const message = failure ? (failure.message || failure.step || '') : '';
                                    const client = proposal.clientName || '';
                                    const user = connectedUserName(members, proposal.userId);
                                    return (
                                        <div key={proposal.id} class='list-group-item'>
                                            <div class='d-flex flex-column flex-md-row gap-2 justify-content-between'>
                                                <div class='flex-fill'>
                                                    <div class='fw-bold'>{proposal.summary}</div>
                                                    <small class='text-body-secondary'>
                                                        <Text id='mcpProposals.by' fields={{ client, user }}>from {client} via {user}</Text>
                                                        {!!proposal.grantRevoked && (
                                                            <span class='badge rounded-pill border bg-body-secondary text-body-secondary fw-normal ms-1'>
                                                                <Text id='mcpProposals.connectionRemoved'>connection removed</Text>
                                                            </span>
                                                        )}
                                                        {' · '}
                                                        {util.formatDate(proposal.createdDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}
                                                    </small>
                                                    {isPartial && (
                                                        <div class='mt-1'>
                                                            <span class='badge bg-warning-subtle text-warning-emphasis rounded-pill'>
                                                                <Text id='mcpProposals.partial'>Partially created</Text>
                                                            </span>
                                                        </div>
                                                    )}
                                                    {!!failure && (
                                                        <div class='small text-danger mt-1'>
                                                            <i class='fa-solid fa-triangle-exclamation me-1' />
                                                            <Text id='mcpProposals.failed' fields={{ message }}>Failed: {message}</Text>
                                                        </div>
                                                    )}
                                                </div>
                                                <div class='d-flex align-items-start gap-2'>
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-primary rounded-pill text-nowrap'
                                                        disabled={busy}
                                                        onClick={() => this.onApply(proposal)}
                                                    >
                                                        {busy && <i class='fa-solid fa-spinner fa-spin me-1' />}
                                                        {isPartial ? (
                                                            <Text id='mcpProposals.continue'>Continue</Text>
                                                        ) : (
                                                            <Text id='mcpProposals.create'>Create</Text>
                                                        )}
                                                    </button>
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-outline-secondary rounded-pill text-nowrap'
                                                        disabled={busy}
                                                        onClick={() => this.onDismiss(proposal)}
                                                    >
                                                        <Text id='mcpProposals.dismiss'>Dismiss</Text>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        );
    }
}

export default ProposalsDrawer;
